import { Component, type ReactNode } from 'react'

/**
 * Catches failures loading a lazy route chunk — every route but the homepage,
 * Announcements and Maintenance is code-split (see App.tsx) — where React's
 * default behavior (Suspense stays on its fallback forever if the dynamic
 * import never settles, or the tree unmounts silently if it rejects) can
 * leave a real user stuck on "Loading…" with no way out. The usual cause is
 * a browser holding a cached page from just before a deploy, pointing at a
 * content-hashed chunk the new deploy no longer serves.
 *
 * That specific failure is fixed by a single reload — the fresh HTML references
 * the current build's hashes — so one is attempted automatically before falling
 * back to a manual retry button for anything a reload doesn't resolve.
 */
const RELOAD_FLAG = 'ark-chunk-reload'

type Props = { children: ReactNode }
type State = { failed: boolean }

export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { failed: false }

  static getDerivedStateFromError(): State {
    return { failed: true }
  }

  componentDidCatch(): void {
    if (sessionStorage.getItem(RELOAD_FLAG)) return
    sessionStorage.setItem(RELOAD_FLAG, '1')
    window.location.reload()
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children

    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-3 bg-base-200 px-4 text-center">
        <p className="text-sm opacity-70">
          This page couldn&apos;t load — this usually means a new version just went live.
        </p>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => {
            sessionStorage.removeItem(RELOAD_FLAG)
            window.location.reload()
          }}
        >
          Reload
        </button>
      </div>
    )
  }
}
